<script lang="ts" setup>
import { z } from 'zod'
import type { FormErrorEvent, FormSubmitEvent } from '#ui/types'
import type { Section } from '~/types'

interface Props extends Pick<Section, 'title' | 'subtitle' | 'description'> {}

const props = defineProps<Props>()

const pb = useNuxtApp().$pb
const resetSchema = z.object({
  email: z.string().email(),
})
type ResetSchema = z.output<typeof resetSchema>
const resetDetails = reactive<ResetSchema>({
  email: '',
})

const loginSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  passwordConfirm: z.string().min(8, 'Password must be at least 8 characters long'),
}).refine(data => data.passwordConfirm === data.password, { message: 'Passwords must match', path: ['passwordConfirm'],
})
type LoginSchema = z.output<typeof loginSchema>
const loginDetails = reactive<LoginSchema>({
  password: '',
  passwordConfirm: '',
})

const route = useRoute()

const toast = useToast()

const hasToken = ref(false)
onMounted(() => {
  resetDetails.email = route.query.email as string
  if (route.query.i) {
    hasToken.value = true
  }
})

async function requestPassword(event: FormSubmitEvent<ResetSchema>) {
  error.value = { title: '', description: '' }

  //  pb.collection(collectionIdOrName).confirmPasswordReset(resetToken, newPassword, newPasswordConfirm, options = {});
  try {
    await pb.collection('users').requestPasswordReset(event.data.email)
    toast.add({
      id: 'password-reset',
      title: 'Password Reset',
      description: 'A password reset link has been sent to your email. This may take a few minutes to arrive.',
      icon: 'ri:check-line',
    })
  }
  catch (err) {
    error.value = {
      title: 'Password Reset Error',
      description: err?.data?.message,
    }
    toast.add({
      id: 'login-error',
      title: 'Password Reset Error',
      description: err?.data?.message,
      color: 'red',
      icon: 'ri:error-warning-line',
    })
  }
}

async function savePassword(event: FormSubmitEvent<LoginSchema>) {
  error.value = { title: '', description: '' }
  //  pb.collection(collectionIdOrName).confirmPasswordReset(resetToken, newPassword, newPasswordConfirm, options = {});
  try {
    await pb.collection('users').confirmPasswordReset(route.query.i as string, event.data.password, event.data.passwordConfirm)
    toast.add({
      id: 'password-reset',
      title: 'Password Saved',
      description: 'Your new password has been saved. Please login.',
      icon: 'ri:check-line',
    })
    navigateTo('/login')
  }
  catch (err: any) {
    const message = err?.response?.data?.token?.message || 'An error occurred while saving your password.'
    error.value = {
      title: 'Password Not Saved',
      description: message + ' Please send a new password reset request.',
    }
    toast.add({
      id: 'login-error',
      title: 'Login Error',
      description: message,
      color: 'red',
      icon: 'ri:error-warning-line',
    })
    showPasswordRequestRedoButton.value = true
  }
}

const passwordVisible = ref(false)

const error = ref({
  title: '',
  description: '',
})

const showPasswordRequestRedoButton = ref(false)
</script>

<template>
  <div>
    <UForm
      v-if="!hasToken"
      :schema="resetSchema"
      :state="resetDetails"
      class="max-w-sm mx-auto"
      @submit="requestPassword"
    >
      <HeadingText
        tag="h3"
        class="pb-6 text-center"
      >
        Request New Password
      </HeadingText>
      <hr class="mb-6">
      <UFormGroup
        label="Email"
        class="mb-3"
        name="email"
        size="lg"
      >
        <UInput
          v-model="resetDetails.email"
          type="text"
          placeholder="Email"
          size="lg"
        />
      </UFormGroup>

      <div class="flex justify-between">
        <UButton
          type="submit"
          icon="gbf:arrow-right"
          trailing
        >
          Request New Password
        </UButton>
      </div>
    </UForm>
    <UForm
      v-else
      :schema="loginSchema"
      :state="loginDetails"
      class="max-w-sm mx-auto"
      @submit="savePassword"
    >
      <HeadingText
        tag="h3"
        class="pb-6 text-center"
      >
        Enter New Password
      </HeadingText>
      <hr class="mb-6">
      <UFormGroup
        label="Password"
        class="mb-3"
        name="password"
      >
        <UButtonGroup
          class="w-full"
          size="lg"
        >
          <UInput
            v-model="loginDetails.password"
            :type="passwordVisible ? 'string' : 'password'"
            placeholder="Password"
            class="flex-1"
            size="lg"
          />
          <UButton
            v-if="passwordVisible"
            tabindex="-1"
            type="button"
            icon="heroicons:eye-slash-16-solid"
            color="gray"
            size="lg"
            aria-label="hide password"
            @click="passwordVisible = !passwordVisible"
          />
          <UButton
            v-else
            tabindex="-1"
            type="button"
            icon="heroicons:eye-16-solid"
            color="gray"
            aria-label="show password"
            size="lg"
            @click="passwordVisible = !passwordVisible"
          />
        </UButtonGroup>
      </UFormGroup>
      <UFormGroup
        label="Confirm Password"
        class="mb-3"
        name="passwordConfirm"
      >
        <UButtonGroup
          class="w-full"
          size="lg"
        >
          <UInput
            v-model="loginDetails.passwordConfirm"
            :type="passwordVisible ? 'string' : 'password'"
            size="lg"
            placeholder="Password"
            class="flex-1"
          />
          <UButton
            v-if="passwordVisible"
            tabindex="-1"
            type="button"
            icon="heroicons:eye-slash-16-solid"
            color="gray"
            size="lg"
            aria-label="hide password"
            @click="passwordVisible = !passwordVisible"
          />
          <UButton
            v-else
            tabindex="-1"
            type="button"
            icon="heroicons:eye-16-solid"
            color="gray"
            aria-label="show password"
            size="lg"
            @click="passwordVisible = !passwordVisible"
          />
        </UButtonGroup>
      </UFormGroup>

      <div class="flex justify-between">
        <UButton
          type="submit"
          :disabled="showPasswordRequestRedoButton"
          icon="gbf:arrow-right"
        >
          Save Password
        </UButton>
      </div>
      <transition
        name="fade"
        mode="out-in"
      >
        <UAlert
          v-if="error.title"
          :title="error.title"
          :description="error.description"
          color="red"
          class="mt-6"
          icon="ri:error-warning-line"
        />
      </transition>
      <transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="showPasswordRequestRedoButton"
          class="flex justify-end"
        >
          <UButton

            type="button"
            icon="gbf:arrow-right"
            trailing
            class="mt-3"
            size="xl"
            @click="hasToken = false"
          >
            Request New Password
          </UButton>
        </div>
      </transition>
    </UForm>
  </div>
</template>
